import { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Check from "@mui/icons-material/Check";
import Download from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import { to12Hour } from "../utils";
import { hourToBusinessMinutes } from "../../../utils/dateUtils";
import logoBremar from "../../../assets/logo-bremar.png";

const CARD_WIDTH = 380;

const SERVICE_TYPE_META = {
  MIP: { label: "MIP", color: "#1D4E89" },
  TICKET: { label: "Ticket", color: "#B45309" },
  NEBULIZACION: { label: "Nebulización", color: "#0F766E" },
  MICROINYECCION: { label: "Microinyección", color: "#6D28D9" },
  MOSCA: { label: "Mosca", color: "#3F6212" },
  ABEJA: { label: "Abeja", color: "#A16207" },
  EQUIPO: { label: "Equipo", color: "#334155" },
  CHOQUE: { label: "Choque", color: "#B91C1C" },
  SUPERVISION: { label: "Supervisión", color: "#0369A1" },
  EVENTUAL: { label: "Eventual", color: "#9A3412" },
  OTRO: { label: "Otro", color: "#57534E" },
  MAINTENANCE: { label: "Mantenimiento", color: "#1D4E89" },
  OFFICE_VISIT: { label: "Visita oficina", color: "#166534" },
  MECHANIC_WORKSHOP: { label: "Taller", color: "#9A3412" },
  MEETING: { label: "Reunión", color: "#6D28D9" },
  LAB_STUDY: { label: "Laboratorio", color: "#9F1239" },
  UNIT_DELIVERY: { label: "Entrega", color: "#0F766E" },
};

const TYPE_PALETTE = [
  "#1D4E89",
  "#0F766E",
  "#B45309",
  "#6D28D9",
  "#9F1239",
  "#3F6212",
  "#0369A1",
  "#9A3412",
  "#334155",
];

const getTypeMeta = (service) => {
  const raw = String(service?.type || service?.eventType || "").trim();
  if (!raw) return { label: "Servicio", color: "#57534E" };

  const key = raw.toUpperCase().replace(/\s+/g, "_");
  if (SERVICE_TYPE_META[key]) return SERVICE_TYPE_META[key];
  if (SERVICE_TYPE_META[raw.toUpperCase()]) return SERVICE_TYPE_META[raw.toUpperCase()];

  const byLabel = Object.values(SERVICE_TYPE_META).find(
    (item) => item.label.toLowerCase() === raw.toLowerCase()
  );
  if (byLabel) return byLabel;

  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) hash = raw.charCodeAt(i) + ((hash << 5) - hash);
  return {
    label: raw,
    color: TYPE_PALETTE[Math.abs(hash) % TYPE_PALETTE.length],
  };
};

const groupByHour = (list) => {
  const grouped = {};
  const noHour = [];
  list.forEach((s) => {
    if (s?.hour) {
      if (!grouped[s.hour]) grouped[s.hour] = [];
      grouped[s.hour].push(s);
    } else {
      noHour.push(s);
    }
  });
  const sortedHours = Object.keys(grouped).sort(
    (a, b) => hourToBusinessMinutes(a) - hourToBusinessMinutes(b)
  );
  return [
    ...sortedHours.map((hour) => ({ title: hour, services: grouped[hour] })),
    ...(noHour.length ? [{ title: "Sin hora", services: noHour }] : []),
  ];
};

function distributeServices(services) {
  const assigned = {};
  const unassigned = [];

  services.forEach((service) => {
    const tech = service?.technician;
    const techId = tech?._id;
    const techName =
      tech?.name || (typeof tech === "string" && tech.trim() ? tech : "");

    if (techId || techName) {
      const key = String(techId || `name-${techName}`);
      if (!assigned[key]) {
        assigned[key] = {
          key,
          title: techName || "Técnico",
          phone: tech?.phone || "",
          services: [],
        };
      }
      assigned[key].services.push(service);
    } else {
      unassigned.push(service);
    }
  });

  const columns = Object.values(assigned)
    .sort((a, b) => a.title.localeCompare(b.title, "es"))
    .map((col) => ({
      ...col,
      services: groupByHour(col.services),
    }));

  if (unassigned.length > 0) {
    columns.push({
      key: "unassigned",
      title: "Sin asignar",
      phone: "",
      isUnassigned: true,
      services: groupByHour(unassigned),
    });
  }

  return columns;
}

const makeProcediaId = (colKey, selectedDay, date) => {
  const seed = `${colKey}|${selectedDay || ""}|${date || ""}`;
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const code = (hash >>> 0).toString(36).toUpperCase().padStart(4, "0").slice(-4);
  const day = String(selectedDay || "00").padStart(2, "0");
  return `PR-${day}-${code}`;
};

const countServices = (groups) =>
  groups.reduce((sum, group) => sum + (group.services?.length || 0), 0);

const slug = (text) =>
  String(text || "procedia")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const writeImageClipboard = async (blob) => {
  if (!navigator.clipboard?.write || !window.ClipboardItem) return false;
  await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
  return true;
};

function ServiceItem({ service }) {
  const type = getTypeMeta(service);
  const isEvent =
    service?._isFromServices !== true && Boolean(service?.eventType);
  const clientName = service?.client?.name || service?.client || "";
  const branchName = service?.branch?.name || service?.branch || "";
  const address = service?.branch?.address || service?.location || "";
  const notes = service?.notes || service?.note || "";
  const place = isEvent
    ? service?.title || type.label
    : [clientName, branchName].filter(Boolean).join("  ·  ") || "Sin sucursal";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "3px 1fr",
        gap: 1.1,
        py: 1.05,
        borderBottom: "1px solid #EDEDED",
      }}
    >
      <Box sx={{ bgcolor: type.color, borderRadius: 99, alignSelf: "stretch" }} />
      <Box sx={{ minWidth: 0, pr: 0.5 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: type.color,
            letterSpacing: "0.02em",
            mb: 0.2,
          }}
        >
            {type.label}
            {isEvent ? "  ·  Evento" : ""}
        </Typography>
        <Typography
          sx={{
            fontSize: 14.5,
            fontWeight: 600,
            color: "#111",
            lineHeight: 1.3,
          }}
        >
          {place}
        </Typography>
        {address ? (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 13,
              color: "#555",
              lineHeight: 1.4,
            }}
          >
            {address}
          </Typography>
        ) : null}
        {notes ? (
          <Typography
            sx={{
              mt: 0.45,
              fontSize: 12.5,
              color: "#666",
              lineHeight: 1.4,
              fontStyle: "italic",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {notes}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function ProcediaCaptureCard({ col, dateLabel, captureRef }) {
  const total = countServices(col.services);

  return (
    <Box
      ref={captureRef}
      data-procedia-capture
      sx={{
        width: "100%",
        bgcolor: "#fff",
        color: "#111",
        fontFamily:
          '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
      }}
    >
      <Box
        sx={{
          px: 2.25,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid #EEE",
          position: "sticky",
          top: 0,
          bgcolor: "#fff",
          zIndex: 1,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.35}>
          <Box
            component="img"
            src={logoBremar}
            alt="Bremar"
            sx={{ width: 28, height: 26, objectFit: "contain" }}
          />
          <Typography
            sx={{
              fontSize: 11,
              color: "#888",
              fontWeight: 600,
              letterSpacing: "0.06em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {col.id}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: "#666", mb: 0.25, textTransform: "capitalize" }}>
          {dateLabel}
        </Typography>
        <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1}>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: "-0.03em",
              lineHeight: 1.2,
              color: "#111",
            }}
          >
            {col.title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#888", fontWeight: 500, flexShrink: 0 }}>
            {total} {total === 1 ? "servicio" : "servicios"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2.25, py: 0.4 }}>
        {col.services.map((group, gIdx) => (
          <Box key={`${col.key}-${gIdx}`} sx={{ py: 1.1 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "#111",
                mb: 0.2,
              }}
            >
              {group.title === "Sin hora" ? "Sin hora" : to12Hour(group.title) || group.title}
            </Typography>
            {group.services.map((service, idx) => (
              <ServiceItem key={service?._id ?? `${gIdx}-${idx}`} service={service} />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function Procedias({
  open,
  services = [],
  onClose,
  repeatStatus,
  selectedDay,
  date,
}) {
  const columnRefs = useRef({});
  const [statusByCol, setStatusByCol] = useState({});
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const columns = useMemo(
    () =>
      distributeServices(services).map((col) => ({
        ...col,
        id: makeProcediaId(col.key, selectedDay, date),
      })),
    [services, selectedDay, date]
  );
  const isProcessing = repeatStatus && repeatStatus.stage !== "completed";
  const dateLabel = selectedDay
    ? `${selectedDay} de ${date || ""}`.trim()
    : date || "Fecha no definida";

  const setColStatus = (key, status) =>
    setStatusByCol((prev) => ({ ...prev, [key]: status }));

  const captureColumn = async (col) => {
    const target = columnRefs.current[col.key];
    if (!target) throw new Error("No se encontró la procedia");

    const clone = target.cloneNode(true);
    clone.style.width = `${CARD_WIDTH}px`;
    clone.style.maxWidth = `${CARD_WIDTH}px`;
    clone.style.position = "absolute";
    clone.style.left = "-12000px";
    clone.style.top = "0";
    clone.style.margin = "0";
    clone.style.transform = "none";
    clone.style.maxHeight = "none";
    clone.style.overflow = "visible";
    clone.style.borderRadius = "0";
    clone.style.boxShadow = "none";
    document.body.appendChild(clone);

    try {
      const canvas = await html2canvas(clone, {
        scale: 2.5,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: CARD_WIDTH,
        windowWidth: CARD_WIDTH,
      });
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen"))),
          "image/png"
        );
      });
      return blob;
    } finally {
      document.body.removeChild(clone);
    }
  };

  const copyColumn = async (col) => {
    if (statusByCol[col.key] === "loading") return;
    setColStatus(col.key, "loading");
    try {
      const blob = await captureColumn(col);
      const copied = await writeImageClipboard(blob).catch(() => false);
      if (!copied) {
        downloadBlob(blob, `${col.id || slug(col.title)}.png`);
      }
      setColStatus(col.key, copied ? "copied" : "downloaded");
      setToast({
        open: true,
        severity: "success",
        message: copied ? "Imagen copiada." : "Se descargó la imagen.",
      });
      setTimeout(() => setColStatus(col.key, "idle"), 2200);
    } catch (err) {
      console.error(err);
      setColStatus(col.key, "error");
      setToast({
        open: true,
        severity: "error",
        message: "No se pudo copiar la imagen.",
      });
      setTimeout(() => setColStatus(col.key, "idle"), 2200);
    }
  };

  const downloadColumn = async (col) => {
    if (statusByCol[col.key] === "loading") return;
    setColStatus(col.key, "loading");
    try {
      const blob = await captureColumn(col);
      downloadBlob(blob, `${col.id || slug(col.title)}.png`);
      setColStatus(col.key, "downloaded");
      setToast({
        open: true,
        severity: "success",
        message: "Imagen descargada.",
      });
      setTimeout(() => setColStatus(col.key, "idle"), 2200);
    } catch (err) {
      console.error(err);
      setColStatus(col.key, "error");
      setToast({
        open: true,
        severity: "error",
        message: "No se pudo descargar la imagen.",
      });
      setTimeout(() => setColStatus(col.key, "idle"), 2200);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        bgcolor: "#D8DCE3",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(15,23,42,0.06)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.7)",
        }}
      >
        <Box minWidth={0}>
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: "#111", letterSpacing: "-0.02em" }}>
            Procedias
          </Typography>
          <Typography sx={{ color: "#6B7280", fontSize: 12.5, mt: 0.1, textTransform: "capitalize" }}>
            {dateLabel}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isProcessing}
          size="small"
          sx={{
            color: "#444",
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
            "&:hover": { bgcolor: "#F9FAFB" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          p: { xs: 2, md: 3 },
          display: "grid",
          gridTemplateColumns: `repeat(auto-fill, ${CARD_WIDTH}px)`,
          justifyContent: "center",
          alignContent: "start",
          gap: 2.5,
          overflow: "auto",
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(15,23,42,0.22)",
            borderRadius: 99,
          },
        }}
      >
        {columns.length === 0 ? (
          <Box sx={{ gridColumn: "1 / -1", m: "auto", textAlign: "center", color: "#6B7280", py: 8 }}>
            <Typography sx={{ fontSize: 14 }}>
              No hay servicios para generar procedias
            </Typography>
          </Box>
        ) : (
          columns.map((col) => {
            const status = statusByCol[col.key] || "idle";
            const busy = status === "loading" || isProcessing;
            const copied = status === "copied";

            return (
              <Box
                key={col.key}
                sx={{
                  width: CARD_WIDTH,
                  height: "min(620px, calc(100dvh - 132px))",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "16px",
                  overflow: "hidden",
                  bgcolor: "#fff",
                  boxShadow:
                    "0 1px 2px rgba(15,23,42,0.06), 0 10px 24px rgba(15,23,42,0.10), 0 28px 48px rgba(15,23,42,0.08)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow:
                      "0 2px 6px rgba(15,23,42,0.08), 0 16px 32px rgba(15,23,42,0.14)",
                  },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    bgcolor: "#fff",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "#D1D5DB",
                      borderRadius: 99,
                    },
                  }}
                >
                  <ProcediaCaptureCard
                    col={col}
                    dateLabel={dateLabel}
                    captureRef={(el) => {
                      columnRefs.current[col.key] = el;
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    bgcolor: "#F7F8FA",
                    borderTop: "1px solid #EEF0F3",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: "#6B7280",
                      letterSpacing: "0.06em",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {col.id}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={copied ? "Copiada" : "Copiar imagen"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => copyColumn(col)}
                          sx={{
                            color: copied ? "#15803D" : "#374151",
                            bgcolor: "#fff",
                            border: "1px solid #E5E7EB",
                            borderRadius: "8px",
                            width: 34,
                            height: 34,
                            boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
                            "&:hover": { bgcolor: "#F9FAFB", borderColor: "#D1D5DB" },
                          }}
                        >
                          {busy ? (
                            <CircularProgress size={14} />
                          ) : copied ? (
                            <Check sx={{ fontSize: 16 }} />
                          ) : (
                            <ContentCopy sx={{ fontSize: 15 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Descargar">
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => downloadColumn(col)}
                          sx={{
                            color: "#374151",
                            bgcolor: "#fff",
                            border: "1px solid #E5E7EB",
                            borderRadius: "8px",
                            width: 34,
                            height: 34,
                            boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
                            "&:hover": { bgcolor: "#F9FAFB", borderColor: "#D1D5DB" },
                          }}
                        >
                          <Download sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={2800}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="outlined"
          sx={{ bgcolor: "#fff", borderRadius: "10px", fontSize: 13, boxShadow: "0 8px 24px rgba(15,23,42,0.12)" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
